import React, { useEffect, useState } from 'react';
import { AuthContainer, AuthPrimaryButton } from '@client/components/AuthComponents';
import { Button } from '@client/components/ui/button';
import { Label } from '@client/components/ui/label';
import { Input } from '@client/components/ui/input';
import { getOauthProviderIcon, selectSession } from '@client';
import { useDispatch, useSelector } from 'react-redux';
import { selectPreLogin } from '@client';
import { fetchAuth0ConnectionTypeByEmailForPublicRequest, resetPreLoginStatus } from '@client';
import { isValidEmailFormat, SessionType } from '@shared';
import { useAuth0 } from '@auth0/auth0-react';
import { useHomeNavigation } from '@client/hooks/useHomeNavigation';
import { useSearchParams } from 'react-router-dom';
import { useAuthorizationParams } from '@client/hooks/useAuthorizationParams';
import { MaintenanceNotice } from '@/components/MaintenanceNotice';
import { getDomainFromEmail } from '@shared';
import { ArrowLeft } from 'lucide-react';
export interface LoginOptions {
  socialConnection: Extract<Auth0Connection, 'google-oauth2' | 'windowslive'> | undefined;
  email?: string;
}

const SHOW_MAINTENANCE_NOTICE = false;

export function Login() {
  const [searchParams] = useSearchParams();
  const emailHint = searchParams.get('email_hint');
  const screenHint = searchParams.get('screen_hint');
  const connectionParam = searchParams.get('connection');
  const overrideConnection = searchParams.get('override_connection');

  const [email, setEmail] = useState<string>(emailHint ?? '');
  const [isValidEmail, setIsValidEmail] = useState(true);
  // Show signup options when user enters a new email (connection === 'none')
  const [showSignupOptions, setShowSignupOptions] = useState(false);

  const {
    status,
    error,
    connection,
    phoneNumber,
    email: preLoginEmail,
  } = useSelector(selectPreLogin);
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const {
    authorizationParams,
    searchParams: { clientSignIn, redirectTo },
  } = useAuthorizationParams();
  const dispatch = useDispatch();
  const session = useSelector(selectSession);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isValidEmail = !!(email && email.length > 0) && isValidEmailFormat(email);
    setIsValidEmail(isValidEmail);
    if (!isValidEmail) {
      return;
    }

    // If showing signup options, proceed to create account with appropriate connection
    if (showSignupOptions) {
      const isInternalUser = getDomainFromEmail(email) === 'joinwaterlily.com';
      const shouldUseEmail = isInternalUser || overrideConnection === 'email';
      const signupConnection = shouldUseEmail ? 'email' : 'sms';

      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          'connection': signupConnection,
          'login_hint': signupConnection === 'sms' ? phoneNumber : email,
          'ext-email': signupConnection === 'sms' ? email : undefined,
        },
        appState: {
          returnTo: redirectTo || '/',
        },
      });
      return;
    }

    dispatch(fetchAuth0ConnectionTypeByEmailForPublicRequest(email));
  };

  const handleSocialLogin = (
    socialConnection: Extract<Auth0Connection, 'google-oauth2' | 'windowslive'>,
  ) => {
    loginWithRedirect({
      authorizationParams: {
        ...authorizationParams,
        connection: socialConnection,
      },
      appState: {
        returnTo: redirectTo || '/',
      },
    });
  };

  const handleBackClick = () => {
    dispatch(resetPreLoginStatus());
    setShowSignupOptions(false);
    setEmail('');
  };

  // Trigger Auth0 login
  useEffect(() => {
    if (status === 'loading' || session.sessionType !== SessionType.unauthenticated) {
      return;
    }
    if (clientSignIn) {
      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          ...(connectionParam && { connection: connectionParam }),
        },
        appState: {
          returnTo: redirectTo || '/',
        },
      });
      return;
    }
    if (status === 'complete' && connection) {
      const finalEmail = preLoginEmail ?? email;

      // No account found - show signup options
      if (connection === 'none') {
        setShowSignupOptions(true);
        return;
      }

      // Account exists - proceed to Auth0 with detected connection
      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          'connection': connection,
          'login_hint': connection === 'sms' ? phoneNumber : finalEmail,
          'ext-email': connection === 'sms' ? finalEmail : undefined,
        },
        appState: {
          returnTo: redirectTo || '/',
        },
      });
    }
  }, [
    status,
    connection,
    authorizationParams,
    emailHint,
    clientSignIn,
    isLoading,
    isAuthenticated,
    phoneNumber,
    email,
  ]);

  useHomeNavigation();

  return (
    <AuthContainer title={showSignupOptions ? 'Create an account' : 'Welcome to Waterlily'}>
      {SHOW_MAINTENANCE_NOTICE && (
        <MaintenanceNotice
          message={
            'Some features may be temporarily unavailable due to scheduled maintenance. We’re working to restore full functionality as quickly as possible.'
          }
          startTime={''}
          endTime={''}
        />
      )}

      <form
        onSubmit={handleSubmit}
        className="space-y-6 pt-4 text-gray-800"
      >
        {/* Social Login Buttons - only show when signup options are visible */}
        {showSignupOptions && (
          <>
            <div className="flex flex-col gap-4">
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full py-6 text-base font-normal"
                onClick={() => handleSocialLogin('google-oauth2')}
              >
                {getOauthProviderIcon('google')}
                Continue with Google
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full py-6 text-base font-normal"
                onClick={() => handleSocialLogin('windowslive')}
              >
                {getOauthProviderIcon('microsoft')}
                Continue with Microsoft
              </Button>
            </div>

            <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
              <span className="text-muted-foreground text-primary-dark relative z-10 bg-white px-2">
                Or continue with email
              </span>
            </div>
          </>
        )}

        <div className="grid gap-2">
          <Label
            htmlFor="email"
            className="text-primary-dark"
          >
            Email
          </Label>

          <Input
            id="email"
            type="email"
            placeholder="advisor@example.com"
            value={email}
            className="rounded-full px-4 py-6 text-base"
            onChange={e => setEmail(e.target.value)}
            disabled={showSignupOptions}
            required
          />
          {!isValidEmail && (
            <p className="text-sm text-red-500">Please enter a valid email address</p>
          )}
        </div>

        {error && <ErrorMessage error={error} />}

        <AuthPrimaryButton
          title={showSignupOptions ? 'Create Account' : 'Continue'}
          onClick={undefined}
          className="py-6 text-base"
          type="submit"
          isLoading={status === 'loading'}
        />
        {showSignupOptions && (
          <Button
            variant="link"
            size="sm"
            onClick={handleBackClick}
            className=" w-full text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="h-3 w-3" />
            Use different email
          </Button>
        )}
      </form>
    </AuthContainer>
  );
}

function ErrorMessage({ error }: { error: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
      <svg
        className="h-4 w-4 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
          clipRule="evenodd"
        />
      </svg>
      <span className="font-medium">{error || 'Something went wrong. Please try again.'}</span>
    </div>
  );
}
